/**
 * What a settings-like page is written in: a titled group, a box of rows, one row (what it is on the
 * left, its value or its control on the right), and a number typed into a row.
 */
import { useEffect, useState, type ReactNode } from 'react'

export const Group = ({ title, note, children }: { title: string; note?: string; children: ReactNode }) => (
  <section>
    <div className="mb-[6px] flex h-4 items-center justify-between text-[12px] tracking-normal">
      <h2 className="m-0 text-[12px] font-normal leading-4 text-ws-label">{title}</h2>
      {note && <span className="text-ws-fg-4">{note}</span>}
    </div>
    {children}
  </section>
)
export const Rows = ({ children }: { children: ReactNode }) => <div className="divide-y divide-ws-hair rounded-[8px] border border-ws-line">{children}</div>
export function Row({ left, title, sub, right, onClick }: { left?: ReactNode; title: string; sub?: string; right?: ReactNode; onClick?: () => void }) {
  const body = (
    <>
      {left}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] leading-[18px] tracking-normal text-ws-fg">{title}</span>
        {sub && <span className="block text-[12px] leading-4 tracking-normal text-ws-fg-3">{sub}</span>}
      </span>
      {right}
    </>
  )
  return onClick ? (
    <button type="button" onClick={onClick} className="flex min-h-10 w-full items-center gap-3 px-3 py-[6px] text-left transition-colors duration-150 hover:bg-ws-hover">
      {body}
    </button>
  ) : (
    <div className="flex min-h-10 items-center gap-3 px-3 py-[6px]">{body}</div>
  )
}
export const value = 'shrink-0 text-[13px] tracking-normal text-ws-fg-2'
export const quiet = 'shrink-0 rounded-[6px] px-2 py-1 text-[12px] tracking-normal text-ws-fg-3 transition-colors duration-150 hover:bg-ws-hover hover:text-ws-fg'
export const note = 'm-0 mt-2 text-[12px] leading-[18px] tracking-normal text-ws-fg-3'

/** A number typed into a row. It is saved when you leave the field or press Enter; a value out of range goes back to what it was. */
export function Num({ value: v, onSave, label, prefix, suffix, min = 0, max, width = 92 }: { value: number; onSave: (n: number) => void; label: string; prefix?: string; suffix?: string; min?: number; max?: number; width?: number }) {
  const [text, setText] = useState(`${v}`)
  useEffect(() => setText(`${v}`), [v])
  const commit = () => {
    const n = Number(text.replace(',', '.'))
    if (text.trim() === '' || !Number.isFinite(n) || n < min || (max != null && n > max)) return setText(`${v}`)
    if (n !== v) onSave(n)
  }
  return (
    <label className="flex h-8 shrink-0 items-center rounded-[8px] border border-ws-line bg-ws-field pl-[10px] pr-2 text-[13px] tracking-normal transition-colors duration-150 focus-within:border-ws-focus" style={{ width }}>
      {prefix && <span className="text-ws-fg-4">{prefix}</span>}
      <input value={text} onChange={(e) => setText(e.target.value.replace(/[^\d.,]/g, ''))} onBlur={commit} onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()} inputMode="decimal" aria-label={label} className="h-full min-w-0 flex-1 pl-1 text-right tabular-nums" />
      {suffix && <span className="pl-1 text-ws-fg-4">{suffix}</span>}
    </label>
  )
}
